import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  assignQuestionsFromTemplate,
  copyAnswersFromPreviousReport,
  createFromExistingTemplate,
  getAllTemplates
} from "../../api/reportsApi";
import { 
  getCentralMonitoringTemplate,
  createCentralMonitoringTemplate,
  createCentralMonitoringReport
} from "../../api/centralMonitoringApi";
import { useNavigate } from "react-router-dom";
import { addTemplate } from "../../api/studyApi";
import AddIcon from "../../components/icons/AddIcon";
import ItemsList from "../../components/lists/ItemsList";
import { FormTemplate, TemplateCategory } from "../../types/Rows";
import AddItemWithExistingPicker, {
  InputType,
} from "../template/AddItemWithExistingPicker";

const Templates = ({
  templates,
  studyId,
}: {
  templates: FormTemplate[];
  studyId: string;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Separate templates by category
  const regularTemplates = templates.filter(
    (x) => x.studyId === studyId && (x.templateType === TemplateCategory.DEFAULT || !x.templateType)
  );

  const { data: centralMonitoringTemplate } = useQuery(
    ["CentralMonitoringTemplate", studyId],
    async () => {
      try {
        return await getCentralMonitoringTemplate(studyId);
      } catch {
        return null;
      }
    },
    { enabled: !!studyId, retry: false }
  );

  const { mutate: addTemplateMutation } = useMutation(
    async (name: string) => {
      await addTemplate(studyId!, name);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["Templates"]);
        queryClient.invalidateQueries(["Studies"]);
      },
    }
  );

  const { mutate: createCentralTemplate } = useMutation(
    async () => {
      await createCentralMonitoringTemplate(studyId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["Templates"]);
        queryClient.invalidateQueries(["Studies"]);
        queryClient.invalidateQueries(["CentralMonitoringTemplate", studyId]);
      },
      onError: () => {
        queryClient.invalidateQueries(["CentralMonitoringTemplate", studyId]);
      },
    }
  );

  const { mutate: createCMReport, isLoading: isCreatingReport } = useMutation(
    async () => createCentralMonitoringReport(studyId),
    {
      onSuccess: (data) => {
        navigate(`/reports/${data.id}`);
      },
    }
  );

  const { data: allTemplates } = useQuery(
    ["Templates"],
    async () => getAllTemplates(),
    {
      enabled: studyId != null,
      initialData: [],
    }
  );

  const { mutate: createFromExisting } = useMutation(
    async (id: string) => {
      const source = allTemplates.find(x=>x.id==id);
      var res = await addTemplate(studyId!,source.name);
      console.log('res is', res);
      await assignQuestionsFromTemplate(res.data, id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["Templates"]);
        queryClient.invalidateQueries(["Studies"]);
      },
    }
  );


  return (
    <div className="p-3">
      {/* Central Monitoring Section */}
      <div className="mb-6 p-4 border border-[var(--sc-border)] rounded-[10px] bg-[var(--sc-bg)]">
        <h2 className="text-[15px] font-semibold text-[var(--sc-text)] mb-3">
          Centralny Monitoring
        </h2>
        {centralMonitoringTemplate ? (
          <div className="flex items-center gap-3 flex-wrap">
            <a 
              href={`/templates/${centralMonitoringTemplate.id}`}
              className="text-sm font-medium text-[var(--sc-text)] underline hover:text-[#F4A832]"
            >
              {centralMonitoringTemplate.name}
            </a>
            <span className="text-xs text-[var(--sc-muted)]">
              (utworzono: {new Date(centralMonitoringTemplate.created).toLocaleDateString()})
            </span>
          </div>
        ) : (
          <button 
            onClick={() => createCentralTemplate()}
            className="rounded-full px-4 py-2 text-sm font-medium bg-[#F4A832] text-[#0F0F0F] hover:bg-[#e09620] transition-colors"
          >
            Utwórz szablon centralnego monitoringu
          </button>
        )}
      </div>

      {/* Regular Templates Section */}
      <ItemsList
        isLoading={false}
        data={regularTemplates}
        urlPrefix="/templates/"
        isSearchVisible
      >
        <div className="flex flex-1 items-end gap-3">
          <h1 className="text-[15px] font-semibold text-[var(--sc-text)]">
            Szablony standardowe
          </h1>{" "}
          <AddItemWithExistingPicker
            text="Dodaj szablon"
            saveNew={(value) => {
              addTemplateMutation(value);
            }}
            saveExisting={(id: string) => createFromExisting(id)}
            inputType={InputType.ALLOWALL}
            items={async () =>
            (allTemplates
                .filter(
                  (x) => x.studyId!=null
                )
                .map((x) => ({
                  id: x.id,
                  name: x.studyName + " - "+ x.name,
                }))) ?? []
            }
          >
            <AddIcon />
          </AddItemWithExistingPicker>
        </div>
      </ItemsList>
    </div>
  );
};

export default Templates;
